import React, { useEffect, useMemo, useRef, useState } from 'react';
import _ from 'lodash';
import { useMutation } from 'react-query';
import classnames from 'classnames';
import {
  Activity,
  ActivityAssociation,
  File,
  GroupEntityLabelKey,
  useGroupEntityLabel,
} from 'v2/models';
import {
  PotentialAssociationsQueryResult,
  searchAssociationsApiCall,
  getUniqueAssociations,
  getAssociationLength,
  getActivityAssociationName,
} from 'v2/utils/activityUtils';
import restFactory from 'v2/utils/restFactory';
import {
  AccordionLayout,
  AssociationsLayout,
} from 'v2/common/Activity/AssociationsLayout';
import styles from './activityAssociations.module.scss';
import SearchBar from '../SearchBar/SearchBar';
import ActivityAssociationsType from './ActivityAssociationsType/ActivityAssociationType';
import ActivityAssociationsTypeContent from './ActivityAssociationsTypeContent/ActivityAssociationsTypeContent';
import pluralize from 'pluralize-esm';

type callBackChanges = (allCheckedFields: ActivityAssociation[]) => void;

export interface ActivityAssociationsProps {
  initialActivityAssociations: ActivityAssociation[];
  activity?: Activity;
  activityType?: string;
  file?: File;
  onChange?: callBackChanges;
  Layout?: AssociationsLayout;
  readOnly?: boolean;
  headerLegend?: string;
}

const apiCall: searchAssociationsApiCall = {
  DataVendor: {
    endpoint: 'data_vendor',
    searchField: 'name__icontains',
    fields: { size: 100 },
  },
  Contact: {
    endpoint: 'contact',
    searchField: 'search',
    fields: { size: 100 },
  },
  DataProduct: {
    endpoint: 'data_product',
    searchField: 'name',
    fields: { size: 100 },
  },
  Searches: {
    endpoint: 'search/details',
    searchField: 'search_text',
    fields: { size: 100, depth: 'master-organization' },
  },
};

const ActivityAssociations = ({
  activity,
  initialActivityAssociations,
  onChange,
  Layout = AccordionLayout,
  readOnly = false,
  headerLegend = 'Association',
  activityType,
  file,
}: ActivityAssociationsProps) => {
  const groupEntityLabel = useGroupEntityLabel();
  const [searchText, setSearchText] = useState('');
  const [currentAssociationType, setCurrentAssociationType] =
    useState<GroupEntityLabelKey>('ALL');

  const initialAssociations = useMemo(
    () =>
      getUniqueAssociations([
        ...(activity?.relationships || file?.relationships || []).map(
          (assoc) => ({
            ...assoc,
            selected: true,
            editable: true,
          })
        ),
        ...(initialActivityAssociations || []).map((assoc) => ({
          selected: true,
          editable: false,
          ...assoc,
        })),
      ]),
    [initialActivityAssociations, activity, file]
  );

  const [localAssociations, setLocalAssociations] =
    useState<ActivityAssociation[]>(initialAssociations);

  const checkedAssociations = useMemo(
    () => _.filter(localAssociations, 'selected'),
    [localAssociations]
  );

  useEffect(() => {
    if (searchText.length > 1) {
      potentialAssociationsMutation();
    } else {
      setLocalAssociations(
        getUniqueAssociations([...checkedAssociations, ...initialAssociations])
      );
    }
  }, [searchText]);

  const initialRender = useRef(true);
  useEffect(() => {
    if (initialRender.current) {
      initialRender.current = false;
      return;
    }

    const loadedAssociations = localAssociations.map((assoc) => ({
      ...assoc,
      selected: false,
    }));
    setLocalAssociations(
      getUniqueAssociations([...initialAssociations, ...loadedAssociations])
    );
  }, [activity, file]);

  function handleSearch(searchText: string) {
    setSearchText(searchText);
  }

  const { mutate: potentialAssociationsMutation } = useMutation({
    mutationKey: [
      'ActivityAssociations_GetAssociations',
      `association_query_${searchText}`,
    ],
    mutationFn: async () => {
      let data: ActivityAssociation[] = [];
      for (const [table, { endpoint, searchField, fields }] of Object.entries(
        apiCall
      )) {
        const { results } =
          await restFactory.get<PotentialAssociationsQueryResult>(
            `/dj/api/v1/${endpoint}/`,
            { ...fields, [searchField]: searchText }
          );
        data = [
          ...data,
          ...results.map(({ id, name, primary_associations }) => ({
            name: getActivityAssociationName(name, primary_associations),
            object_id: id,
            table_name: table,
            selected: false,
            editable: true,
          })),
        ];
      }
      return data;
    },
    onSuccess(data) {
      if (data) {
        setLocalAssociations(
          getUniqueAssociations([
            ...checkedAssociations,
            ...data,
            ...initialAssociations,
          ])
        );
      }
    },
  });

  function checkBoxChanged(
    isChecked: boolean,
    association: ActivityAssociation
  ) {
    if (readOnly || !association.editable) {
      return;
    }

    const newAssociations = localAssociations.map((assoc) => {
      let associa = { ...assoc };
      if (assoc === association) {
        associa = {
          ...assoc,
          selected: isChecked,
          is_mandatory: Boolean(assoc.is_mandatory),
        };
        if (!file) {
          delete associa.is_mandatory;
        }
      }
      return associa;
    });
    setLocalAssociations(newAssociations);
    onChange?.(_.filter(newAssociations, 'selected'));
  }

  function associationsContent() {
    const label = groupEntityLabel[currentAssociationType];
    return (
      <div className={styles.container}>
        <ActivityAssociationsType
          currentAssociationType={currentAssociationType}
          setCurrentAssociationType={setCurrentAssociationType}
          checkedAssociations={checkedAssociations}
        />
        <div className={styles.associationContainer}>
          <div className={classnames(styles.association)}>
            <SearchBar
              placeHolder={`Search ${label.toLocaleLowerCase()}`}
              onSearch={handleSearch}
              currentText={searchText}
              classNameBar={styles.searchBox}
              classNameIcon={styles.searchIcon}
              showOwnIcon={true}
              lengthTextValidation={{
                number: 1,
                operator: '!==',
                debounce: 500,
              }}
            />
            <ActivityAssociationsTypeContent
              currentAssociationType={currentAssociationType}
              localAssociations={localAssociations}
              checkedAssociations={checkedAssociations}
              file={file}
              activityType={activityType}
              checkBoxChanged={checkBoxChanged}
            />
          </div>
        </div>
      </div>
    );
  }
  const associationsLength = getAssociationLength(checkedAssociations);

  return (
    <Layout
      header={`${associationsLength} ${pluralize(
        headerLegend,
        associationsLength
      )}`}
      content={associationsContent()}
      onShow={() => {
        setCurrentAssociationType('ALL');
      }}
    />
  );
};

export default ActivityAssociations;
